import { LiveTableProps } from "../../../types";
import useGetMe from "../../../../../hooks/useCurrentUser";
import { useEffect, useState } from "react";
import { formatDateView } from "../../../../adiqEdi/detalhesEdi/utils";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import { Button, TableContainer } from "../../../styles";
import Table, { TableColumn } from "../../../../../components/Table";
import {
  formatDivisionRuleView,
  formatEffectContractStatusView,
  formatEffectTypeView,
  formatMoneyView,
} from "../../../../../utils/formatViews";
import { EyeOutlined } from "@ant-design/icons";
import {
  ContractEffectBankRecords,
  ContractEffectHistoryRecords,
} from "../modals/types";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { BankModal } from "../modals/bankModal";
import { HistoryModal } from "../modals/historyModal";
import { Checkbox } from "antd";
import ContractEffectsService from "../../../effectsFulfilled/contractEffectsService";

export const LiveTable: React.FC<LiveTableProps> = ({
  data,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const user = useGetMe();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formattedDate = (date: string) => formatDateView(date);
  const [bankModalOpen, setBankModalOpen] = useState<boolean>(false);
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const contractEffectsService = new ContractEffectsService();
  const [bankContractData, setBankContractData] =
    useState<ContractEffectBankRecords>({
      hash: "",
      records: [],
    });
  const [historyContractData, setHistoryContractData] =
    useState<ContractEffectHistoryRecords>({
      hash: "",
      records: [],
    });
  const [payedUr, setPayedUr] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allUnpaidHashes, setAllUnpaidHashes] = useState<string[]>([]);
  const [paidHashes, setPaidHashes] = useState<string[]>([]);
  const [statusBHashes, setStatusBHashes] = useState<string[]>([]);
  const [otherStatusHashes, setOtherStatusHashes] = useState<string[]>([]);
  const [newlySelectedHashes, setNewlySelectedHashes] = useState<string[]>([]);

  const handleOpenBankModal = async (hashIdentifier: string) => {
    try {
      setIsLoading(true);
      setBankModalOpen(true);
      const response = await contractEffectsService.getContractEffectPayment(
        hashIdentifier
      );

      if (response.length > 0)
        setBankContractData({
          hash: hashIdentifier,
          records: response,
        });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseBankModal = () => {
    setBankModalOpen(false);
    setBankContractData({
      hash: "",
      records: [],
    });
  };

  const handleOpenHistoryModal = async (hashIdentifier: string) => {
    try {
      setIsLoading(true);
      setHistoryModalOpen(true);
      const response = await contractEffectsService.getContractEffectHistory(
        hashIdentifier
      );

      if (response.length > 0)
        setHistoryContractData({
          hash: hashIdentifier,
          records: response,
        });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setBankContractData({
      hash: "",
      records: [],
    });
  };

  return (
    <div>
      <LoadingOverlay show={isLoading} relative />

      <TableContainer>
        <Table
          dataSource={data.records}
          pagination={{
            defaultPageSize: pageSize,
            defaultCurrent: currentPage,
            current: currentPage,
            onShowSizeChange: (_, size) => {
              onPageSizeChange(size);
            },
            total: data.count,
            onChange: (pageNum) => onPageChange(pageNum),
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} efeitos`,
            showSizeChanger: true,
          }}
        >
          <TableColumn
            title="Código do Lojista"
            dataIndex="merchantId"
            key="merchantId"
          />
          <TableColumn
            title="Usuario final recebedor"
            dataIndex="finalReceiverUser"
            key="finalReceiverUser"
          />
          <TableColumn
            title="Razão Social"
            dataIndex="businessName"
            key="businessName"
          />
          <TableColumn
            title="Data de liquidação"
            dataIndex="settlementDate"
            key="settlementDate"
            render={formatDateView}
          />
          <TableColumn
            title="Tipo de efeito"
            dataIndex="effectType"
            key="effectType"
            render={formatEffectTypeView}
          />
          <TableColumn
            title="Valor da UR"
            dataIndex="urValue"
            key="urValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Regra de divisão"
            dataIndex="divisionRules"
            key="divisionRules"
            render={formatDivisionRuleView}
          />
          <TableColumn
            title="Valor Transferido Credor"
            dataIndex="creditorTransferredValue"
            key="creditorTransferredValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Valor Transferido Cliente"
            dataIndex="clientTransferredValue"
            key="clientTransferredValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Arranjo"
            dataIndex="paymentArrangement"
            key="paymentArrangement"
          />
          <TableColumn
            title="Referência Externa"
            dataIndex="externalReference"
            key="externalReference"
          />
          <TableColumn
            title="CNPJ Credor"
            dataIndex="holderDocumentNumber"
            key="holderDocumentNumber"
          />
          <TableColumn
            title="Status"
            dataIndex="status"
            key="status"
            render={formatEffectContractStatusView}
          />
          <TableColumn
            title="Dados Bancarios do Efeito"
            dataIndex="visualizar"
            key="visualizar"
            render={(_: any, transaction: any) =>
              transaction.hashIdentifier ? (
                <EyeOutlined
                  style={{
                    fontSize: "20px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    handleOpenBankModal(transaction.hashIdentifier)
                  }
                />
              ) : null
            }
          />
          <TableColumn
            title="Rastreio do Efeito"
            dataIndex="visualizar"
            key="visualizar"
            render={(_: any, transaction: any) =>
              transaction.hashIdentifier ? (
                <EyeOutlined
                  style={{
                    fontSize: "20px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    handleOpenHistoryModal(transaction.hashIdentifier)
                  }
                />
              ) : null
            }
          />
        </Table>
      </TableContainer>

      <BankModal
        isShowing={bankModalOpen}
        modalData={bankContractData}
        onClose={handleCloseBankModal}
      />

      <HistoryModal
        isShowing={historyModalOpen}
        modalData={historyContractData}
        onClose={handleCloseHistoryModal}
      />
    </div>
  );
};

export default LiveTable;
